import React, { useMemo, useState } from 'react';
import { Box, Divider, Paper, Stack, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery } from '@mui/material';
import {
  DETAIL_LEVELS,
  DETAIL_LEVEL_META,
  atLeastLevel,
  isDetailLevel,
  type DetailLevel,
} from '../../../support/aiEraReport/shared/detail-level';
import type { ReportConfig, ReportModel, ReportSectionConfig } from '../../../support/reportView/types';
import { ensureLayout, defaultSizeForType, GRID_COLS } from '../../../support/reportView/layout';
import { getSectionDefinition, makeReportTheme, type ReportTheme } from './sections';
import ReportGrid, { type ReportGridItem } from './ReportGrid';
import DownloadReportButton from './pdf/DownloadReportButton';

/**
 * Renders a report COMPOSITION (`ReportConfig`, authored by an admin / seeded)
 * over an engine-produced DATA BAG (`ReportModel`). It is entirely generic:
 * every section is looked up in the section registry, gated by the current
 * detail level, and handed its slice of `model.data`. Nothing here knows about
 * the AI-Era engine — that engine simply produces a `ReportModel` and a config.
 *
 * The detail level is a local, on-screen choice; it never changes the model,
 * only which sections are shown. A role toggle appears at the Technical level
 * for any section whose data carries per-role bodies.
 */

type Props = {
  config: ReportConfig;
  model: ReportModel;
  dark?: boolean;
  /** Slot for extra actions beside the built-in Download PDF button. */
  actions?: React.ReactNode;
  /** Show the built-in "Download PDF" button (config-driven export). Default on. */
  enablePdf?: boolean;
  /** Filename for the exported PDF. */
  pdfFileName?: string;
};

const SectionCard: React.FC<{ title?: string; hint?: string; theme: ReportTheme; children: React.ReactNode }> = ({
  title,
  hint,
  theme,
  children,
}) => (
  <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, height: '100%', bgcolor: theme.surface, borderColor: theme.grid }}>
    {(title || hint) && (
      <Box mb={1.5}>
        {title && (
          <Typography variant="subtitle1" sx={{ color: theme.ink, fontWeight: 700 }}>
            {title}
          </Typography>
        )}
        {hint && (
          <Typography variant="caption" sx={{ color: theme.inkMuted, display: 'block', mt: 0.25 }}>
            {hint}
          </Typography>
        )}
      </Box>
    )}
    {children}
  </Paper>
);

const ReportRenderer: React.FC<Props> = ({ config, model, dark, actions, enablePdf = true, pdfFileName }) => {
  const theme = useMemo(() => makeReportTheme(dark), [dark]);
  const showToggle = config.detailLevels !== false;

  const initialLevel: DetailLevel = isDetailLevel(config.defaultDetailLevel)
    ? (config.defaultDetailLevel as DetailLevel)
    : 'business';
  const [level, setLevel] = useState<DetailLevel>(initialLevel);
  const [roleView, setRoleView] = useState<string>('securityEngineer');

  // Role framing follows the source's rule: only the Technical level exposes
  // the engineer/manager toggle; shallower levels read in manager framing.
  const effectiveRole = level === 'technical' ? roleView : 'portfolioManager';

  // Whether the composition carries any authored geometry. Legacy configs (none)
  // keep the exact vertical <Stack>; a placed config renders on the static grid.
  const hasGeometry = useMemo(() => config.sections.some((s) => s.layout), [config]);
  // Below a comfortable width the grid would be unusable, so we fall back to the
  // single-column stack (which is also what mobile has always shown).
  const wideEnough = useMediaQuery('(min-width:900px)');
  const useGrid = hasGeometry && wideEnough;

  // Back-fill geometry so every section has a placement when we render the grid;
  // building `visible` off the ensured config keeps section order and content
  // identical to the stack path.
  const ensured = useMemo(() => (useGrid ? ensureLayout(config) : config), [config, useGrid]);

  // Only the sections that survive level gating AND produce content. Feeding the
  // grid just these (with vertical compaction) means holes left by hidden or
  // empty sections close up rather than leaving gaps.
  const visible = ensured.sections
    .map((section: ReportSectionConfig) => {
      const def = getSectionDefinition(section.type);
      if (!def) return null;
      const minLevel = isDetailLevel(section.minLevel) ? (section.minLevel as DetailLevel) : def.defaultMinLevel;
      if (!atLeastLevel(level, minLevel)) return null;

      const dataKey = section.dataKey ?? def.defaultDataKey;
      const data = dataKey ? model.data[dataKey] : undefined;
      const content = def.render({ config: section, data, model, level, theme, roleView: effectiveRole });
      if (content == null || content === false) return null;

      const title = section.title ?? def.defaultTitle;
      const node =
        def.chrome === 'bare' ? (
          <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, height: '100%', bgcolor: theme.surface, borderColor: theme.grid }}>
            {content}
          </Paper>
        ) : (
          <SectionCard title={title} hint={section.hint} theme={theme}>
            {content}
          </SectionCard>
        );
      return { section, node };
    })
    .filter(Boolean) as Array<{ section: ReportSectionConfig; node: React.ReactNode }>;

  const gridItems: ReportGridItem[] = visible.map(({ section, node }) => {
    const size = defaultSizeForType(section.type);
    const layout = section.layout ?? { x: 0, y: 0, w: size.w, h: size.h };
    return {
      id: section.id,
      layout,
      content: <Box sx={{ height: '100%', overflow: 'auto' }}>{node}</Box>,
    };
  });

  const anyRoleSection = config.sections.some(
    (s) => getSectionDefinition(s.type)?.type === 'recommendations'
  );

  return (
    <Stack spacing={3}>
      {(showToggle || actions || enablePdf) && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 }, bgcolor: theme.surface, borderColor: theme.grid }}>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2}>
            {showToggle && (
              <>
                <Typography variant="overline" sx={{ color: theme.inkMuted }}>
                  Detail level
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={level}
                  onChange={(_e, v) => v && setLevel(v)}
                  sx={{
                    '& .MuiToggleButton-root': { color: theme.inkMuted, borderColor: theme.grid },
                    '& .Mui-selected': {
                      color: `${theme.ink ?? 'inherit'} !important`,
                      bgcolor: dark ? 'rgba(2,200,255,0.15)' : undefined,
                    },
                  }}
                >
                  {DETAIL_LEVELS.map((l) => (
                    <ToggleButton key={l} value={l} title={DETAIL_LEVEL_META[l].blurb}>
                      {DETAIL_LEVEL_META[l].label}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </>
            )}
            {level === 'technical' && anyRoleSection && (
              <Box display="flex" alignItems="center" gap={1}>
                <Typography variant="caption" sx={{ color: theme.inkMuted }}>
                  Read as:
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={roleView}
                  onChange={(_e, v) => v && setRoleView(v)}
                  sx={{ '& .MuiToggleButton-root': { color: theme.inkMuted, borderColor: theme.grid } }}
                >
                  <ToggleButton value="portfolioManager">Portfolio Manager</ToggleButton>
                  <ToggleButton value="securityEngineer">Security Engineer</ToggleButton>
                </ToggleButtonGroup>
              </Box>
            )}
            {(enablePdf || actions) && (
              <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 1 }}>
                {enablePdf && (
                  <DownloadReportButton
                    config={config}
                    model={model}
                    level={level}
                    roleView={effectiveRole}
                    dark={dark}
                    fileName={pdfFileName}
                  />
                )}
                {actions}
              </Box>
            )}
          </Box>
          {showToggle && (
            <>
              <Divider sx={{ my: 1.5, borderColor: theme.grid }} />
              <Typography variant="caption" sx={{ color: theme.inkMuted }}>
                <Box component="span" sx={{ color: theme.ink }}>
                  {DETAIL_LEVEL_META[level].audience}.
                </Box>{' '}
                {DETAIL_LEVEL_META[level].blurb} Same data at every level — only the amount shown changes.
              </Typography>
            </>
          )}
        </Paper>
      )}
      {useGrid ? (
        <ReportGrid items={gridItems} cols={config.gridCols ?? GRID_COLS} interactive={false} />
      ) : (
        <Stack spacing={3}>
          {visible.map(({ section, node }) => (
            <Box key={section.id}>{node}</Box>
          ))}
        </Stack>
      )}
    </Stack>
  );
};

export default ReportRenderer;
